import { Fragment, useCallback, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiAlertOutline, mdiChevronDown, mdiChevronRight, mdiDownload, mdiInformationOutline } from '@mdi/js'
import { PageHeader } from '../shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'
import { Card, CardContent } from '../ui/card'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { AppIcon } from '../ui/icon'
import { EmptyState } from '../ui/empty-state'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet'
import { cn } from '../../lib/utils'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useFormatters } from '../../lib/format'
import { useLocale } from '../../contexts/LocaleContext'
import { ExpensesViewTabs } from './ExpensesViewTabs'
import { useAsset } from '../../hooks/useQueries'
import { useApi } from '../../api/hooks'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from '../ApiSearchableSelect'
import {
  pickLabel,
  useExpenseCategories,
  useExpenseStatement,
  useExpenseStatementLines,
  type ExpenseCategoryTaxonomy,
  type ExpenseStatement,
  type ExpenseStatementColumn,
  type ExpenseStatementRow,
  type StatementAmount,
  type StatementQuery,
  type StatementRegime,
  type StatementView,
} from '../../hooks/useExpenseStatement'

/** The URL state of the statement, validated by the route. */
export interface ExpenseStatementSearch {
  from?: string
  to?: string
  site?: string
  /** A building of the site: the statement then reads that building alone. */
  building?: string
  view?: StatementView
  regime?: StatementRegime
  amount?: StatementAmount
}

type Preset = 'last_12_months' | 'year_to_date' | 'last_year' | 'custom'

const ROW_UNCLASSIFIED = 'unclassified'
const ROW_UNMAPPED = 'unmapped'
// The asset types a site or a building can be (shipped ontology codes).
const SITE_TYPE_CODES = 'building_group,building,house'
const BUILDING_TYPE_CODES = 'building,house'

function iso(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** A YYYY-MM-DD string as a local calendar day (new Date() would read it as UTC midnight). */
function localDay(isoDay: string): Date {
  const [y, m, d] = isoDay.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d)
}

/** The window of a preset, computed from today in the reader's time zone. */
function presetWindow(preset: Exclude<Preset, 'custom'>, today = new Date()): { from: string; to: string } {
  const y = today.getFullYear()
  switch (preset) {
    case 'year_to_date':
      return { from: `${y}-01-01`, to: iso(today) }
    case 'last_year':
      return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` }
    default: {
      // The day after the same calendar day a year ago. Building the date
      // from its parts keeps 29 February from rolling into March twice.
      const from = new Date(y - 1, today.getMonth(), today.getDate() + 1)
      if (today.getMonth() === 1 && today.getDate() === 29) from.setTime(new Date(y - 1, 2, 1).getTime())
      return { from: iso(from), to: iso(today) }
    }
  }
}

function presetOf(from: string, to: string): Preset {
  for (const p of ['last_12_months', 'year_to_date', 'last_year'] as const) {
    const w = presetWindow(p)
    if (w.from === from && w.to === to) return p
  }
  return 'custom'
}

/** A row group: a family and its categories (business view), or one flat group of accounts. */
interface RowGroup {
  key: string
  label: string
  rows: ExpenseStatementRow[]
  cells: Record<string, number>
  total: number
}

function groupRows(st: ExpenseStatement, taxonomy: ExpenseCategoryTaxonomy | undefined, locale: string, unclassifiedLabel: string): RowGroup[] {
  if (st.view === 'accounting') {
    return [{ key: '_accounts', label: '', rows: st.rows, cells: {}, total: 0 }]
  }
  const groups: RowGroup[] = []
  const byFamily = new Map<string, RowGroup>()
  for (const row of st.rows) {
    const family = row.family || ROW_UNCLASSIFIED
    let g = byFamily.get(family)
    if (!g) {
      const fam = taxonomy?.families.find((f) => f.code === family)
      g = { key: family, label: fam ? pickLabel(fam.label, locale) : unclassifiedLabel, rows: [], cells: {}, total: 0 }
      byFamily.set(family, g)
      groups.push(g)
    }
    g.rows.push(row)
    g.total += row.total
    for (const [col, v] of Object.entries(row.cells)) g.cells[col] = (g.cells[col] || 0) + v
  }
  return groups
}

export function ExpenseStatementPage() {
  const { org = 'demo' } = useParams({ strict: false }) as { org?: string }
  const search = useSearch({ from: '/$org/expenses/statement' }) as ExpenseStatementSearch
  const navigate = useNavigate({ from: '/$org/expenses/statement' })
  const { t } = useTranslation('expenses')
  const { locale } = useLocale()
  const { formatMoney } = useFormatters()

  const defaults = presetWindow('last_12_months')
  const query: StatementQuery = {
    from: search.from || defaults.from,
    to: search.to || defaults.to,
    siteUid: search.building || search.site,
    view: search.view || 'business',
    regime: search.regime || 'all',
    amount: search.amount || 'ttc',
  }
  const preset = presetOf(query.from, query.to)

  const setSearch = (patch: Partial<ExpenseStatementSearch>) =>
    navigate({ search: (prev: ExpenseStatementSearch) => ({ ...prev, ...patch }) })

  const { data: statement, isLoading, error } = useExpenseStatement(org, query)
  const { data: taxonomy } = useExpenseCategories(org)

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const [cell, setCell] = useState<{ row?: string; column?: string } | null>(null)

  const breadcrumbs = useMemo(() => [
    { title: t('common:nav.home', 'Home'), path: `/${org}` },
    { title: t('list.title'), path: `/${org}/expenses` },
    { title: t('statement.title') },
  ], [org, t])
  useBreadcrumbs(breadcrumbs)

  const categoryLabel = (code: string): string => {
    if (code === ROW_UNCLASSIFIED) return t('statement.row_unclassified')
    if (code === ROW_UNMAPPED) return t('statement.row_unmapped')
    const c = taxonomy?.categories.find((x) => x.code === code)
    return c ? pickLabel(c.label, locale) : code
  }
  const rowLabel = (row: ExpenseStatementRow): string => {
    if (statement?.view === 'accounting') {
      if (row.key === ROW_UNMAPPED) return t('statement.row_unmapped')
      return `${row.key} · ${pickLabel(row.label, locale)}`
    }
    return categoryLabel(row.key)
  }
  const columnLabel = (col: ExpenseStatementColumn): string => {
    if (col.kind === 'site_common') return t('statement.column_site_common')
    if (col.kind === 'unassigned') return t('statement.column_unassigned')
    return col.name || t('list.asset_unknown')
  }

  const currency = statement?.currency || undefined
  const money = (v: number | undefined) => (v ? formatMoney(v, currency) : '—')

  const groups = useMemo(
    () => (statement ? groupRows(statement, taxonomy, locale, t('statement.row_unclassified')) : []),
    [statement, taxonomy, locale, t],
  )

  const { data: site } = useAsset(org, search.site || '')
  const siteName = (site as { name?: string } | undefined)?.name
  const { data: building } = useAsset(org, search.building || '')
  const buildingName = (building as { name?: string } | undefined)?.name

  // The pickers search the assets API: sites are the root sites of the tree
  // (a site group, a standalone building or house), buildings are the
  // buildings and houses under the chosen site.
  const { request } = useApi()
  const searchAssets = useCallback(async (q: string, extra: Record<string, string>): Promise<ApiSearchableSelectOption[]> => {
    const params: Record<string, string> = { limit: '50', sort: 'name', ...extra }
    if (q) params.q = q
    try {
      const res = await request('/api/v1/organizations/{orgCode}/assets', 'GET', { params: { path: { orgCode: org }, query: params } })
      return (((res as { data?: Array<{ uid: string; name?: string }> })?.data) || []).map((a) => ({ value: a.uid, label: a.name || a.uid }))
    } catch {
      return []
    }
  }, [org, request])
  const searchSites = useCallback((q: string) => searchAssets(q, { parent_asset_uid: 'null', asset_type_code: SITE_TYPE_CODES }), [searchAssets])
  const searchBuildings = useCallback(
    (q: string) => (search.site ? searchAssets(q, { ancestor_uid: search.site, asset_type_code: BUILDING_TYPE_CODES }) : Promise.resolve([])),
    [searchAssets, search.site],
  )

  const jurisdictionNotes = useMemo(() => {
    if (!statement || statement.view !== 'accounting' || !taxonomy) return []
    return statement.jurisdictions.map((j) => ({ code: j, chart: taxonomy.jurisdictions[j] }))
  }, [statement, taxonomy])

  const exportCsv = () => {
    if (!statement) return
    const sep = ';'
    const num = (v: number | undefined) => {
      const s = ((v || 0) / 100).toFixed(2)
      return locale.startsWith('fr') ? s.replace('.', ',') : s
    }
    // A name starting like a formula would run in a spreadsheet: neutralize it.
    const quote = (s: string) => `"${(/^[=+\-@\t\r]/.test(s) ? `'${s}` : s).replace(/"/g, '""')}"`
    const header = [t('statement.csv_row'), ...statement.columns.map(columnLabel), t('statement.total')].map(quote)
    const lines = statement.rows.map((r) => [quote(rowLabel(r)), ...statement.columns.map((c) => num(r.cells[c.key])), num(r.total)].join(sep))
    const footer = [quote(t('statement.total')), ...statement.columns.map((c) => num(statement.column_totals[c.key])), num(statement.total)].join(sep)
    const blob = new Blob(['﻿' + [header.join(sep), ...lines, footer].join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `expense-statement-${statement.view}-${statement.from}-${statement.to}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // A cell exists when the server sent its key, even at zero (an invoice
  // draft not priced yet): it stays openable. An absent key is an empty cell.
  const cellButton = (value: number | undefined, row: string | undefined, column: string | undefined, testid: string, label: string, strong = false) => {
    const present = value !== undefined
    return (
      <button
        type="button"
        className={cn(
          'w-full text-right tabular-nums rounded px-1 transition-colors',
          present ? 'hover:bg-muted cursor-pointer' : 'text-muted-foreground/50 cursor-default',
          strong && 'font-semibold',
        )}
        disabled={!present}
        onClick={() => setCell({ row, column })}
        aria-label={present ? `${label} : ${formatMoney(value, currency)}` : undefined}
        data-testid={testid}
      >
        {present ? formatMoney(value, currency) : '—'}
      </button>
    )
  }

  return (
    <div className="space-y-6" data-testid="expense-statement">
      <PageHeader
        icon={SECTION_ICONS.expenses}
        title={t('statement.title')}
        description={t('statement.subtitle')}
        actions={
          <Button
            variant="outline"
            onClick={exportCsv}
            disabled={!statement || statement.line_count === 0}
            aria-label={t('statement.export_csv')}
            title={t('statement.export_csv')}
            data-testid="statement-export-csv"
          >
            <AppIcon path={mdiDownload} size="sm" className="md:mr-2" />
            <span className="hidden md:inline">{t('statement.export_csv')}</span>
          </Button>
        }
      />

      <ExpensesViewTabs active="statement" />

      {/* Filters — every choice lives in the URL. */}
      <div className="flex flex-wrap items-end gap-3" data-testid="statement-filters">
        <AssetPicker
          label={t('statement.site')}
          value={search.site}
          valueName={siteName}
          placeholder={t('statement.all_sites_placeholder')}
          onSearch={searchSites}
          onChange={(uid) => setSearch({ site: uid || undefined, building: undefined })}
          testid="statement-site"
        />
        <AssetPicker
          key={search.site || 'none'}
          label={t('statement.building')}
          value={search.building}
          valueName={buildingName}
          placeholder={search.site ? t('statement.all_buildings') : t('statement.pick_site_first')}
          onSearch={searchBuildings}
          onChange={(uid) => setSearch({ building: uid || undefined })}
          disabled={!search.site}
          testid="statement-building"
        />
        <div className="space-y-1">
          <span className="text-xs text-muted-foreground">{t('statement.period')}</span>
          <Select
            value={preset}
            onValueChange={(v) => {
              if (v !== 'custom') setSearch(presetWindow(v as Exclude<Preset, 'custom'>))
            }}
          >
            <SelectTrigger className="w-48" data-testid="statement-period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="last_12_months" data-testid="statement-period-last_12_months">{t('statement.preset_last_12_months')}</SelectItem>
              <SelectItem value="year_to_date" data-testid="statement-period-year_to_date">{t('statement.preset_year_to_date')}</SelectItem>
              <SelectItem value="last_year" data-testid="statement-period-last_year">{t('statement.preset_last_year')}</SelectItem>
              <SelectItem value="custom" disabled>{t('statement.preset_custom')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <label className="space-y-1">
          <span className="block text-xs text-muted-foreground">{t('statement.from')}</span>
          <input
            type="date"
            value={query.from}
            max={query.to}
            onChange={(e) => e.target.value && setSearch({ from: e.target.value })}
            className="h-10 rounded-md border bg-background px-3 text-sm"
            data-testid="statement-from"
          />
        </label>
        <label className="space-y-1">
          <span className="block text-xs text-muted-foreground">{t('statement.to')}</span>
          <input
            type="date"
            value={query.to}
            min={query.from}
            onChange={(e) => e.target.value && setSearch({ to: e.target.value })}
            className="h-10 rounded-md border bg-background px-3 text-sm"
            data-testid="statement-to"
          />
        </label>
        <Segmented
          label={t('statement.rows')}
          value={query.view}
          onChange={(v) => setSearch({ view: v as StatementView })}
          options={[
            { value: 'business', label: t('statement.view_business') },
            { value: 'accounting', label: t('statement.view_accounting') },
          ]}
          testid="statement-view"
        />
        <Segmented
          label={t('statement.regime')}
          value={query.regime}
          onChange={(v) => setSearch({ regime: v as StatementRegime })}
          options={[
            { value: 'all', label: t('statement.regime_all') },
            { value: 'current', label: t('statement.regime_current') },
            { value: 'works', label: t('statement.regime_works') },
          ]}
          testid="statement-regime"
        />
        <Segmented
          label={t('statement.amount')}
          value={query.amount}
          onChange={(v) => setSearch({ amount: v as StatementAmount })}
          options={[
            { value: 'ttc', label: t('statement.amount_ttc') },
            { value: 'ht', label: t('statement.amount_ht') },
          ]}
          testid="statement-amount"
        />
      </div>

      {statement && <StatementNotices statement={statement} money={money} onOpen={(row) => setCell({ row })} />}

      {statement && jurisdictionNotes.some((j) => !j.chart || !j.chart.validated) && (
        <p className="flex items-start gap-2 text-xs text-muted-foreground" data-testid="statement-chart-note">
          <AppIcon path={mdiInformationOutline} size="xs" className="mt-0.5 shrink-0" />
          {jurisdictionNotes.map((j) => (j.chart
            ? t('statement.chart_not_validated', { country: j.code, chart: j.chart.chart })
            : t('statement.chart_missing', { country: j.code }))).join(' ')}
        </p>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-64" data-testid="statement-loading">
              <div className="motion-safe:animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64 gap-2" data-testid="statement-error">
              <p className="text-destructive">{t('statement.error')}</p>
              <p className="text-sm text-muted-foreground">{(error as any)?.detail || (error as any)?.title || ''}</p>
            </div>
          ) : !statement || statement.line_count === 0 ? (
            <EmptyState
              variant="inline"
              live
              className="h-64 justify-center"
              icon={SECTION_ICONS.expenses}
              title={t('statement.empty')}
              data-testid="statement-empty"
            />
          ) : (
            <div className="overflow-x-auto" data-testid="statement-table-wrap">
              <table className="w-full text-sm" data-testid="statement-table">
                <thead className="bg-muted">
                  <tr>
                    <th className="sticky left-0 z-10 bg-muted px-4 py-3 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground min-w-40 md:min-w-56">
                      {statement.view === 'accounting' ? t('statement.column_account') : t('statement.column_category')}
                    </th>
                    {statement.columns.map((col) => (
                      <th key={col.key} className="px-3 py-3 text-right text-[11px] font-medium uppercase tracking-wider text-muted-foreground min-w-32" data-testid={`statement-col-${col.key}`}>
                        {statement.group_by === 'site' && col.uid ? (
                          <button
                            type="button"
                            className="hover:text-foreground hover:underline normal-case text-xs"
                            onClick={() => setSearch({ site: col.uid, building: undefined })}
                            title={t('statement.drill_site')}
                            data-testid={`statement-drill-${col.key}`}
                          >
                            {columnLabel(col)}
                          </button>
                        ) : col.uid ? (
                          <Link
                            to="/$org/assets/$assetUid"
                            params={{ org, assetUid: col.uid }}
                            className="hover:text-foreground hover:underline normal-case text-xs"
                          >
                            {columnLabel(col)}
                          </Link>
                        ) : (
                          <span className="normal-case text-xs">{columnLabel(col)}</span>
                        )}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-right text-[11px] font-medium uppercase tracking-wider text-muted-foreground min-w-32">
                      {t('statement.total')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {groups.map((g) => {
                    const isCollapsed = !!collapsed[g.key]
                    const flat = statement.view === 'accounting' || g.key === ROW_UNCLASSIFIED
                    return (
                      <Fragment key={g.key}>
                        {!flat && (
                          <tr className="bg-muted" data-testid={`statement-family-${g.key}`}>
                            <td className="sticky left-0 z-10 bg-muted px-4 py-2">
                              <button
                                type="button"
                                className="flex items-center gap-1 font-medium text-foreground"
                                onClick={() => setCollapsed((c) => ({ ...c, [g.key]: !c[g.key] }))}
                                aria-expanded={!isCollapsed}
                                data-testid={`statement-family-toggle-${g.key}`}
                              >
                                <AppIcon path={isCollapsed ? mdiChevronRight : mdiChevronDown} size="sm" />
                                {g.label}
                              </button>
                            </td>
                            {statement.columns.map((col) => (
                              <td key={col.key} className="px-3 py-2 text-right tabular-nums font-medium">{money(g.cells[col.key])}</td>
                            ))}
                            <td className="px-4 py-2 text-right tabular-nums font-semibold">{money(g.total)}</td>
                          </tr>
                        )}
                        {!isCollapsed && g.rows.map((row) => {
                          const alert = row.key === ROW_UNCLASSIFIED || row.key === ROW_UNMAPPED
                          return (
                            <tr key={row.key} className={cn(alert && 'bg-amber-50 dark:bg-amber-950')} data-testid={`statement-row-${row.key}`}>
                              <td className={cn('sticky left-0 z-10 px-4 py-2', alert ? 'bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-400 font-medium' : 'bg-card', !flat && 'pl-10')}>
                                {alert && <AppIcon path={mdiAlertOutline} size="xs" className="mr-1 inline" />}
                                {rowLabel(row)}
                                <span className="ml-2 text-xs text-muted-foreground">{t('statement.line_count', { count: row.line_count })}</span>
                              </td>
                              {statement.columns.map((col) => (
                                <td key={col.key} className="px-2 py-1">
                                  {cellButton(row.cells[col.key], row.key, col.key, `statement-cell-${row.key}-${col.key}`, `${rowLabel(row)} · ${columnLabel(col)}`)}
                                </td>
                              ))}
                              <td className="px-3 py-1">{cellButton(row.total, row.key, undefined, `statement-row-total-${row.key}`, `${rowLabel(row)} · ${t('statement.total')}`, true)}</td>
                            </tr>
                          )
                        })}
                      </Fragment>
                    )
                  })}
                </tbody>
                <tfoot className="border-t-2 border-border bg-muted">
                  <tr data-testid="statement-totals">
                    <td className="sticky left-0 z-10 bg-muted px-4 py-3 font-semibold">{t('statement.total')}</td>
                    {statement.columns.map((col) => (
                      <td key={col.key} className="px-2 py-2">
                        {cellButton(statement.column_totals[col.key], undefined, col.key, `statement-col-total-${col.key}`, `${t('statement.total')} · ${columnLabel(col)}`, true)}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right tabular-nums font-bold" data-testid="statement-grand-total">{money(statement.total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <StatementCellSheet
        org={org}
        query={query}
        cell={cell}
        title={cell ? [cell.row && (statement?.view === 'accounting'
          ? rowLabel(statement.rows.find((r) => r.key === cell.row) || { key: cell.row, cells: {}, total: 0, line_count: 0 })
          : categoryLabel(cell.row)),
        cell.column && statement && columnLabel(statement.columns.find((c) => c.key === cell.column) || { key: cell.column, name: '', kind: 'site' })]
          .filter(Boolean).join(' · ') : ''}
        categoryLabel={categoryLabel}
        onClose={() => setCell(null)}
      />
    </div>
  )
}

function AssetPicker({ label, value, valueName, placeholder, onSearch, onChange, disabled, testid }: {
  label: string
  value?: string
  valueName?: string
  placeholder: string
  onSearch: (q: string) => Promise<ApiSearchableSelectOption[]>
  onChange: (uid: string) => void
  disabled?: boolean
  testid: string
}) {
  const initial = value && valueName ? [{ value, label: valueName }] : []
  return (
    <div className="space-y-1">
      <span className="block text-xs text-muted-foreground">{label}</span>
      <ApiSearchableSelect
        // The select reads its initial options once: remount it when the name
        // of a value that came from the URL arrives.
        key={valueName || 'pending'}
        value={value || ''}
        onValueChange={onChange}
        onSearch={onSearch}
        initialOptions={initial}
        placeholder={placeholder}
        minSearchLength={0}
        disabled={disabled}
        className="w-56"
        data-testid={testid}
      />
    </div>
  )
}

function Segmented({ label, value, onChange, options, testid }: {
  label: string
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
  testid: string
}) {
  return (
    <div className="space-y-1">
      <span className="block text-xs text-muted-foreground">{label}</span>
      <div role="radiogroup" aria-label={label} className="inline-flex h-10 items-center rounded-md bg-muted p-1 text-muted-foreground" data-testid={testid}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={value === o.value}
            onClick={() => onChange(o.value)}
            className={cn(
              'rounded-sm px-3 py-1.5 text-sm font-medium transition-all',
              value === o.value ? 'bg-background text-foreground shadow-sm' : 'hover:text-foreground',
            )}
            data-testid={`${testid}-${o.value}`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )
}

/** What a figure is built on: what could not be classified, still to review, guessed or left out. */
function StatementNotices({ statement, money, onOpen }: {
  statement: ExpenseStatement
  money: (v: number | undefined) => string
  onOpen: (row: string) => void
}) {
  const { t } = useTranslation('expenses')
  const notes: { key: string; text: string; tone: 'warn' | 'info'; row?: string }[] = []
  if (statement.unclassified.line_count > 0) {
    notes.push({
      key: 'unclassified', tone: 'warn', row: statement.view === 'accounting' ? ROW_UNMAPPED : ROW_UNCLASSIFIED,
      text: t('statement.notice_unclassified', { count: statement.unclassified.line_count, amount: money(statement.unclassified.amount) }),
    })
  }
  if (statement.drafts.line_count > 0) {
    notes.push({ key: 'drafts', tone: 'info', text: t('statement.notice_drafts', { count: statement.drafts.line_count, amount: money(statement.drafts.amount) }) })
  }
  if (statement.dated_by_fallback > 0) {
    notes.push({ key: 'dated', tone: 'info', text: t('statement.notice_dated_by_fallback', { count: statement.dated_by_fallback }) })
  }
  if (statement.amount === 'ht' && statement.missing_net > 0) {
    notes.push({ key: 'net', tone: 'warn', text: t('statement.notice_missing_net', { count: statement.missing_net }) })
  }
  if (statement.mixed_currency) {
    notes.push({ key: 'currency', tone: 'warn', text: t('statement.notice_mixed_currency') })
  }
  if (notes.length === 0) return null

  return (
    <div className="flex flex-wrap gap-2" data-testid="statement-notices">
      {notes.map((n) => {
        const cls = cn(
          'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs',
          n.tone === 'warn'
            ? 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300'
            : 'border-border bg-muted/40 text-muted-foreground',
        )
        const body = (
          <>
            <AppIcon path={n.tone === 'warn' ? mdiAlertOutline : mdiInformationOutline} size="xs" />
            {n.text}
          </>
        )
        return n.row ? (
          <button key={n.key} type="button" className={cn(cls, 'hover:underline')} onClick={() => onOpen(n.row!)} data-testid={`statement-notice-${n.key}`}>
            {body}
          </button>
        ) : (
          <span key={n.key} className={cls} data-testid={`statement-notice-${n.key}`}>{body}</span>
        )
      })}
    </div>
  )
}

function StatementCellSheet({ org, query, cell, title, categoryLabel, onClose }: {
  org: string
  query: StatementQuery
  cell: { row?: string; column?: string } | null
  title: string
  categoryLabel: (code: string) => string
  onClose: () => void
}) {
  const { t } = useTranslation('expenses')
  const { locale } = useLocale()
  const { formatMoney } = useFormatters()
  const { data, isLoading } = useExpenseStatementLines(org, query, cell)
  const lines = data?.data || []

  return (
    <Sheet open={!!cell} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="statement-lines-sheet">
        <SheetHeader>
          <SheetTitle>{title || t('statement.lines_title')}</SheetTitle>
          <SheetDescription>{t('statement.lines_description', { count: lines.length })}</SheetDescription>
        </SheetHeader>
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="motion-safe:animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
          </div>
        ) : (
          <ul className="divide-y divide-border mt-2 px-4" data-testid="statement-lines">
            {lines.map((l) => (
              <li key={l.uid} className="py-3 flex items-start justify-between gap-4" data-testid={`statement-line-${l.uid}`}>
                <div className="min-w-0 space-y-1">
                  <Link
                    to="/$org/expenses/$expenseUid"
                    params={{ org, expenseUid: l.uid }}
                    className="font-medium text-foreground hover:text-primary hover:underline"
                    data-testid={`statement-line-link-${l.uid}`}
                  >
                    {l.label || '—'}
                  </Link>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span>
                      {localDay(l.date).toLocaleDateString(locale)}
                      {l.dated_by_fallback && ` (${t('statement.dated_by_creation')})`}
                    </span>
                    {l.asset && (
                      <Link to="/$org/assets/$assetUid" params={{ org, assetUid: l.asset.uid }} className="hover:underline">
                        {l.asset.name}
                      </Link>
                    )}
                    {l.supplier_name && <span>{l.supplier_name}</span>}
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    <Badge variant="outline" className="text-[11px] font-normal">{categoryLabel(l.category)}</Badge>
                    {l.category_source !== 'unclassified' && <Badge variant="outline" className="text-[11px] font-normal text-muted-foreground">
                      {t(`category.source_${l.category_source}`)}
                    </Badge>}
                    {l.regime === 'works' && <Badge variant="secondary" className="text-[11px]">{t('statement.regime_works')}</Badge>}
                    {l.draft && <Badge variant="secondary" className="text-[11px]">{t('statement.draft')}</Badge>}
                  </div>
                </div>
                <span className="text-sm font-medium tabular-nums whitespace-nowrap">
                  {formatMoney(query.amount === 'ht' && l.amount_without_tax != null ? l.amount_without_tax : l.amount, l.currency)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </SheetContent>
    </Sheet>
  )
}
